import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Modal, Button, Popover } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

import { plusicon, refreshicon } from '../../../components/imagepath';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import AdvancedTable from '../../../components/AdvancedTable';
import { matchPermissions } from '../../../utils/Permission';
import { ActionDeleteBooking, ActionGetBookingList } from '../../../store/Actions/BookingAction';

const { confirm } = Modal;

const RoomBookingList = () => {
    const { loader, bookingList } = useSelector(state => state?.BookingManagement);
    const { userDetails } = useSelector(state => state?.auth);
    const [Refresh, setRefresh] = useState(false);
    const pageSize = 10;
    const dispatch = useDispatch();
    const location = useLocation();
    const params = queryString.parse(location.search);

    const matchPermissionsForUser = matchPermissions(userDetails);
    // Assuming these permissions or using generics for now
    const canCreate = true; // matchPermissionsForUser('bookings.create');
    const canUpdate = true; // matchPermissionsForUser('bookings.update');
    const canDelete = true; // matchPermissionsForUser('bookings.delete');

    // UseEffect disabled as fetchData handles initial load and pagination
    // useEffect(() => {
    //     dispatch(ActionGetBookingList({ Query: `?page=${params?.page || 1}` }));
    //     setRefresh(false);
    // }, [dispatch, params?.page, Refresh]);

    const showDeleteConfirm = item => {
        confirm({
            title: `Are you sure delete booking for ${item?.patientId?.name}?`,
            icon: <ExclamationCircleFilled />,
            content: '',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            zIndex: 1200,
            centered: true,
            onOk() {
                dispatch(
                    ActionDeleteBooking({ Query: '/' + (item?._id || item?.id) }, Res => {
                        if (Res === 200) {
                            setRefresh(true);
                        }
                    })
                );
            },
            onCancel() { },
        });
    };

    const content = item => (
        <div className="">
            {canUpdate && (
                <Link to={`/room-booking/edit-booking/${item?._id || item?.id}`} className="btn btn-sm btn-white text-success me-2">
                    <i className="far fa-edit me-1" /> Edit
                </Link>
            )}
            {canDelete && (
                <Link to="#" className="btn btn-sm btn-white text-danger" onClick={() => showDeleteConfirm(item)}>
                    <i className="far fa-trash-alt me-1" /> Delete
                </Link>
            )}
        </div>
    );

    const columns = [
        {
            id: 'sr',
            name: 'Sr',
            selector: 'sr',
            sortable: false,
            cell: (record, index) => {
                const currentPage = parseInt(params?.page) || 1;
                const calculatedIndex = (currentPage - 1) * pageSize + index + 1;
                return <span>{calculatedIndex}</span>;
            },
        },
        {
            id: 'patientName',
            name: 'Patient Name',
            selector: row => row?.patientId?.name,
            sortable: true,
        },
        {
            id: 'roomNo',
            name: 'Room No',
            selector: row => row?.currentRoom?.roomId?.roomNo,
            sortable: true,
        },
        {
            id: 'roomType',
            name: 'Room Type',
            selector: row => row?.currentRoom?.roomTypeId?.name?.en,
            sortable: true,
        }, {
            id: 'occupancy',
            name: 'Occupancy',
            selector: row => row?.currentRoom?.occupancyModeId?.code,
            sortable: true
        },
        {
            id: 'admissionDate',
            name: 'Admission Date',
            selector: row => new Date(row?.admissionDate).toLocaleDateString(),
            sortable: true,
        },
        {
            id: 'dischargeDate',
            name: 'Discharge Date',
            selector: row => new Date(row?.dischargeDate).toLocaleDateString(),
            sortable: true,
        },
        {
            id: 'status',
            name: 'Status',
            selector: 'status',
            sortable: true,
            cell: row => (
                <span className={`badge badge-pill ${row?.status === 'UPCOMING' ? 'bg-success-light' : 'bg-primary-light'}`}>
                    {row?.status}
                </span>
            )
        },
        {
            id: 'actions',
            name: 'Action',
            cell: (record) => (
                <Popover placement="left" content={() => content(record)} trigger="click">
                    <Button type="dashed">• • •</Button>
                </Popover>
            ),
        }
    ];

    const fetchData = useCallback(async (tableParams) => {
        return new Promise((resolve, reject) => {
            dispatch(ActionGetBookingList({ Query: `?page=${tableParams.page || 1}&limit=${tableParams.perPage || 10}` }, (data, error) => {
                if (error) {
                    // Even on error, we resolve to avoid crashing table
                    resolve({ data: [], total: 0 });
                } else {
                    resolve({
                        data: data?.docs || [],
                        total: data?.totalDocs || 0,
                    });
                }
            }));
        });
    }, [dispatch]);

    return (
        <>
            <Header />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/room-booking">Room Management</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Bookings</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    <div className="page-table-header mb-2">
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <div className="doctor-table-blk">
                                                    <h3>Booking List</h3>
                                                    <div className="doctor-search-blk">
                                                        <div className="add-group">
                                                            {canCreate && (
                                                                <Link to="/room-booking/add-booking" className="btn btn-primary add-pluss ms-2">
                                                                    <img src={plusicon} alt="#" />
                                                                </Link>
                                                            )}
                                                            <Link onClick={() => setRefresh(true)} className="btn btn-primary doctor-refresh ms-2">
                                                                <img src={refreshicon} alt="#" />
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <AdvancedTable
                                        resource="bookings"
                                        columns={columns}
                                        fetchData={fetchData}
                                        loading={loader}
                                        enableSearch={true}
                                        defaultPageSize={10}
                                        enableColumnManager={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoomBookingList;
